// import React, { useEffect, useRef, useState } from "react";

// interface StickyHorizontalScrollbarProps {
//     scrollContainerRef: React.RefObject<HTMLElement>;
//     height?: number;
// }

// export const StickyHorizontalScrollbar: React.FC<StickyHorizontalScrollbarProps> = ({
//     scrollContainerRef,
//     height = 14,
// }) => {
//     const barRef = useRef<HTMLDivElement>(null);
//     const spacerRef = useRef<HTMLDivElement>(null);
//     const isSyncingRef = useRef(false);

//     const [rect, setRect] = useState({ left: 0, width: 0 });
//     const [contentWidth, setContentWidth] = useState(0);
//     const [visible, setVisible] = useState(false);

//     // keep the fake bar's position/width matched to the real container on screen
//     useEffect(() => {
//         const container = scrollContainerRef.current;
//         if (!container) return;

//         const updateGeometry = () => {
//             const el = scrollContainerRef.current;
//             if (!el) return;

//             const domRect = el.getBoundingClientRect();
//             setRect({ left: domRect.left, width: domRect.width });

//             const scrollWidth = el.scrollWidth;
//             setContentWidth(scrollWidth);

//             // only show the fixed bar when there's actually something to scroll,
//             // and while the real container is at least partly visible vertically
//             const overflows = scrollWidth > domRect.width + 1;
//             const inViewport = domRect.bottom > 0 && domRect.top < window.innerHeight;
//             setVisible(overflows && inViewport);
//         };

//         updateGeometry();

//         const resizeObserver = new ResizeObserver(updateGeometry);
//         resizeObserver.observe(container);

//         const mutationObserver = new MutationObserver(updateGeometry);
//         mutationObserver.observe(container, { childList: true, subtree: true });

//         window.addEventListener("scroll", updateGeometry, { passive: true });
//         window.addEventListener("resize", updateGeometry);

//         return () => {
//             resizeObserver.disconnect();
//             mutationObserver.disconnect();
//             window.removeEventListener("scroll", updateGeometry);
//             window.removeEventListener("resize", updateGeometry);
//         };
//     }, [scrollContainerRef]);

//     // sync: real container scroll -> fake bar
//     useEffect(() => {
//         const container = scrollContainerRef.current;
//         const bar = barRef.current;
//         if (!container || !bar) return;

//         const onContainerScroll = () => {
//             if (isSyncingRef.current) return;
//             isSyncingRef.current = true;
//             bar.scrollLeft = container.scrollLeft;
//             isSyncingRef.current = false;
//         };

//         container.addEventListener("scroll", onContainerScroll, { passive: true });
//         return () => container.removeEventListener("scroll", onContainerScroll);
//     }, [scrollContainerRef]);

//     // sync: fake bar scroll -> real container
//     const onBarScroll = () => {
//         if (isSyncingRef.current) return;
//         const container = scrollContainerRef.current;
//         const bar = barRef.current;
//         if (!container || !bar) return;
//         isSyncingRef.current = true;
//         container.scrollLeft = bar.scrollLeft;
//         isSyncingRef.current = false;
//     };

//     if (!visible) return null;

//     return (
//         <div
//             ref={barRef}
//             onScroll={onBarScroll}
//             style={{
//                 position: "fixed",
//                 bottom: 0,
//                 left: rect.left,
//                 width: rect.width,
//                 height,
//                 overflowX: "scroll",
//                 overflowY: "hidden",
//                 zIndex: 1300,
//                 background: "#fff",
//             }}
//         >
//             <div ref={spacerRef} style={{ width: contentWidth, height: 1 }} />
//         </div>
//     );
// };

import React, { useCallback, useEffect, useRef, useState } from "react";

interface StickyHorizontalScrollbarProps {
    scrollContainerRef: React.RefObject<HTMLElement>;
    height?: number;
}

export const StickyHorizontalScrollbar: React.FC<StickyHorizontalScrollbarProps> = ({
    scrollContainerRef,
    height = 14,
}) => {
    const [barEl, setBarEl] = useState<HTMLDivElement | null>(null);
    const spacerRef = useRef<HTMLDivElement>(null);
    const isSyncingRef = useRef(false);

    const [rect, setRect] = useState({ left: 0, width: 0 });
    const [contentWidth, setContentWidth] = useState(0);
    const [visible, setVisible] = useState(false);

    // keep the fake bar's position/width matched to the real container on screen
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const updateGeometry = () => {
            const el = scrollContainerRef.current;
            if (!el) return;

            const domRect = el.getBoundingClientRect();
            setRect({ left: domRect.left, width: domRect.width });

            const scrollWidth = el.scrollWidth;
            setContentWidth(scrollWidth);

            const overflows = scrollWidth > domRect.width + 1;
            const inViewport = domRect.bottom > 0 && domRect.top < window.innerHeight;
            setVisible(overflows && inViewport);
        };

        updateGeometry();

        const resizeObserver = new ResizeObserver(updateGeometry);
        resizeObserver.observe(container);

        const mutationObserver = new MutationObserver(updateGeometry);
        mutationObserver.observe(container, { childList: true, subtree: true });

        window.addEventListener("scroll", updateGeometry, { passive: true });
        window.addEventListener("resize", updateGeometry);

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener("scroll", updateGeometry);
            window.removeEventListener("resize", updateGeometry);
        };
    }, [scrollContainerRef]);

    // sync: real container scroll -> fake bar
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !barEl) return;
        barEl.scrollLeft = container.scrollLeft;

        const onContainerScroll = () => {
            if (isSyncingRef.current) return;
            isSyncingRef.current = true;
            barEl.scrollLeft = container.scrollLeft;
            isSyncingRef.current = false;
        };

        container.addEventListener("scroll", onContainerScroll, { passive: true });
        return () => container.removeEventListener("scroll", onContainerScroll);
    }, [scrollContainerRef, barEl]);

    const onBarScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            if (isSyncingRef.current) return;
            const container = scrollContainerRef.current;
            if (!container) return;
            isSyncingRef.current = true;
            container.scrollLeft = e.currentTarget.scrollLeft;
            isSyncingRef.current = false;
        },
        [scrollContainerRef]
    );

    if (!visible) return null;

    return (
        <div
            ref={setBarEl}
            onScroll={onBarScroll}
            style={{
                position: "fixed",
                bottom: 0,
                left: rect.left,
                width: rect.width,
                height,
                overflowX: "scroll",
                overflowY: "hidden",
                zIndex: 1300,
                background: "#fff",
            }}
        >
            <div ref={spacerRef} style={{ width: contentWidth, height: 1 }} />
        </div>
    );
};